import { FC, useEffect, useMemo } from "react";

import { StudentOutcomeScoreTable } from "./StudentOutcomeScoreTable";
import { StudentScoreOverviewTable } from "./StudentScoreOverviewTable";
import { useFetchAndSelectMyAssessments, useFetchAssessmentData } from "./assessment-data";

import { ActivityType } from "components/server-types";
import { t } from "i18n/i18n";
import { EmptyCard } from "mds/components/EmptyCard";
import { AssessPointsMarkerIcon } from "mds/icons";
import { storeApi, useAppSelector } from "store/index";
import {
  activitySelectors,
  pageOutcomeSelectors,
  selectCurrentSectionId,
  selectCurrentUserId,
  selectFullSectionOutcomes,
  selectPagesWithAssessmentsForUserId,
  userSelectors,
} from "store/selectors";
import { useListSelector } from "store/store-hooks";

type StudentPerformanceDashboardProps = {
  userId: string | null;
};

export const StudentPerformanceDashboard: FC<StudentPerformanceDashboardProps> = ({ userId }) => {
  const currentUserId = useAppSelector(selectCurrentUserId);
  const sectionId = useAppSelector(selectCurrentSectionId);
  const sectionOutcomes = useAppSelector(selectFullSectionOutcomes);

  const pageOutcomes = useAppSelector(pageOutcomeSelectors.selectAll);
  const activities = useListSelector(activitySelectors.selectAll);
  const studentUser = useAppSelector((s) => userSelectors.selectById(s, userId));
  const isViewingOwnAssessments = currentUserId === userId;

  // This includes page groups and submissions
  useFetchAssessmentData();
  useFetchAndSelectMyAssessments();

  useEffect(() => {
    if (sectionId) {
      storeApi.page_outcomes.list({ section_id: sectionId });
    }
  }, [sectionId]);

  const activitiesById = useMemo(
    () =>
      activities.reduce(
        (acc, activity) => ({
          ...acc,
          [activity.id]: activity,
        }),
        {} as Record<string, ActivityType>,
      ),
    [activities],
  );

  const pagesWithAssessments = useAppSelector((s) =>
    selectPagesWithAssessmentsForUserId(s, userId, sectionId),
  );
  const allAssessments = pagesWithAssessments.map((p) => p.assessments).flat();
  const allOutcomeAssessments = allAssessments.map((a) => a.outcome_assessments || []).flat();

  if (pagesWithAssessments.length === 0) {
    return (
      <EmptyCard
        className="assessment-dashboard mb-4"
        description={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_description"
            : "assessment_dashboard.all.empty_description_instructor",
          {
            studentName: studentUser?.first_name || t("assessment_dashboard.all.unknown_student"),
          },
        )}
        icon={<AssessPointsMarkerIcon className="text-blue" />}
        title={t(
          isViewingOwnAssessments
            ? "assessment_dashboard.all.empty_title"
            : "assessment_dashboard.all.empty_title_instructor",
        )}
      />
    );
  }

  return (
    <main className="assessment-dashboard overflow-hidden">
      <StudentScoreOverviewTable
        activitiesById={activitiesById}
        pageOutcomes={pageOutcomes}
        pagesWithAssessments={pagesWithAssessments}
        sectionOutcomes={sectionOutcomes}
      />

      <StudentOutcomeScoreTable
        activitiesById={activitiesById}
        allOutcomeAssessments={allOutcomeAssessments}
        pageOutcomes={pageOutcomes}
        pagesWithAssessments={pagesWithAssessments}
        sectionOutcomes={sectionOutcomes}
      />
    </main>
  );
};
